import { RouterProvider } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTokenRefreshInterval } from '@/shared/hooks/useTokenRefreshInterval';
import { initializeSessionCleanupListener } from '@/shared/utils/sessionCleanup';
import { useAppSelector } from '@/shared/store/hooks';
import {
  selectIsAuthInitialized,
  selectIsAuthenticated,
  selectPermissionsLoaded,
} from '@/shared/store/slices/authSlice';
import { InactivityDialog } from '@/shared/components/InactivityDialog';
import { router } from './routes/router';

export default function App() {
  const { checkAuthStatus } = useAuth();
  const isInitialized = useAppSelector(selectIsAuthInitialized);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const permissionsLoaded = useAppSelector(selectPermissionsLoaded);
  const needsAuthCheck =
    !isInitialized || (isAuthenticated && !permissionsLoaded);

  // Start proactive token refresh every 10 minutes
  // Access token TTL is 15 minutes, so refreshing every 10 minutes
  // ensures token never expires during active usage
  useTokenRefreshInterval();

  // Initialize auth state on app startup, including persisted sessions
  // that came back authenticated without permissions.
  useEffect(() => {
    if (needsAuthCheck) {
      checkAuthStatus();
    }
  }, [checkAuthStatus, needsAuthCheck]);

  // Initialize session cleanup listener (sends logout on tab close)
  // This ensures HttpOnly cookies are cleared when the tab closes
  useEffect(() => {
    // console.log('[App] Initializing session cleanup listener');
    const cleanup = initializeSessionCleanupListener();
    return cleanup;
  }, []);

  // Show app-level loading state during auth initialization
  if (!isInitialized) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mb-4"></div>
        <p className="text-lg font-medium text-gray-700 mb-2">
          Initializing Application
        </p>
        <p className="text-sm text-gray-500">
          Please wait while we verify your authentication...
        </p>
      </div>
    );
  }

  return (
    <>
      {isAuthenticated && <InactivityDialog />}
      <Suspense
        fallback={
          <div className="flex items-center justify-center h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            <span className="ml-4 text-lg font-medium">Loading...</span>
          </div>
        }
      >
        <RouterProvider router={router} />
      </Suspense>
    </>
  );
}
